import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/common/AppShell";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import ReportsListPage from "@/pages/ReportsListPage";
import ReportDetailPage from "@/pages/ReportDetailPage";
import UploadReportPage from "@/pages/UploadReportPage";
import GenerateReportPage from "@/pages/GenerateReportPage";
import AdminPage from "@/pages/AdminPage";
import ProfilePage from "@/pages/ProfilePage";
import OpsUploadPage from "@/pages/OpsUploadPage";
import OpsUploadsPage from "@/pages/OpsUploadsPage";
import OpsUploadDetailPage from "@/pages/OpsUploadDetailPage";
import OpsDashboardPage from "@/pages/OpsDashboardPage";
import LiteReportPage from "@/pages/LiteReportPage";
import AgentProfilePage from "@/pages/AgentProfilePage";
import AgentScorecardPage from "@/pages/AgentScorecardPage";
import TimeToConvertPage from "@/pages/TimeToConvertPage";
import LeadFunnelPage from "@/pages/LeadFunnelPage";
import DispositionDeepDivePage from "@/pages/DispositionDeepDivePage";
import DataQualityPage from "@/pages/DataQualityPage";
import RetentionPage from "@/pages/RetentionPage";
import PeakHoursPage from "@/pages/PeakHoursPage";
import CampaignsPage from "@/pages/CampaignsPage";
import DiagnosePage from "@/pages/DiagnosePage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import LaafficParityPage from "@/pages/LaafficParityPage";
import LaafficDispositionsPage from "@/pages/LaafficDispositionsPage";
import LaafficBackfillPage from "@/pages/LaafficBackfillPage";
import LaafficUnmappedPage from "@/pages/LaafficUnmappedPage";
import LaafficIntegrationPage from "@/pages/LaafficIntegrationPage";
import LeadsPage from "@/pages/LeadsPage";
import { Loader2 } from "lucide-react";
import "@/App.css";

function Protected({ children, adminOnly = false, opsBlocked = false }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !(user.is_admin || user.is_super_admin)) return <Navigate to="/" replace />;
  // Ops users cannot access finance pages
  if (opsBlocked && user.role === "ops" && !user.is_admin && !user.is_super_admin) {
    return <Navigate to="/ops" replace />;
  }
  return <AppShell>{children}</AppShell>;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user?.role === "ops" && !user.is_admin && !user.is_super_admin) {
    return <Navigate to="/ops" replace />;
  }
  return <DashboardPage />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<Protected><HomeRedirect /></Protected>} />
          <Route path="/reports" element={<Protected opsBlocked><ReportsListPage /></Protected>} />
          <Route path="/reports/:id" element={<Protected opsBlocked><ReportDetailPage /></Protected>} />
          <Route path="/upload" element={<Protected opsBlocked><UploadReportPage /></Protected>} />
          <Route path="/generate" element={<Protected opsBlocked><GenerateReportPage /></Protected>} />
          <Route path="/ops" element={<Protected><OpsDashboardPage /></Protected>} />
          <Route path="/ops/lite-report" element={<Protected><LiteReportPage /></Protected>} />
          <Route path="/ops/upload" element={<Protected><OpsUploadPage /></Protected>} />
          <Route path="/ops/uploads" element={<Protected><OpsUploadsPage /></Protected>} />
          <Route path="/ops/uploads/:id" element={<Protected><OpsUploadDetailPage /></Protected>} />
          <Route path="/ops/agents/:agentId" element={<Protected><AgentProfilePage /></Protected>} />
          <Route path="/ops/scorecard" element={<Protected><AgentScorecardPage /></Protected>} />
          <Route path="/ops/time-to-convert" element={<Protected><TimeToConvertPage /></Protected>} />
          <Route path="/ops/lead-funnel" element={<Protected><LeadFunnelPage /></Protected>} />
          <Route path="/ops/disposition" element={<Protected><DispositionDeepDivePage /></Protected>} />
          <Route path="/ops/data-quality" element={<Protected><DataQualityPage /></Protected>} />
          <Route path="/ops/retention" element={<Protected><RetentionPage /></Protected>} />
          <Route path="/ops/peak-hours" element={<Protected><PeakHoursPage /></Protected>} />
          <Route path="/ops/leads" element={<Protected><LeadsPage /></Protected>} />
          <Route path="/campaigns" element={<Protected><CampaignsPage /></Protected>} />
          <Route path="/admin" element={<Protected adminOnly><AdminPage /></Protected>} />
          <Route path="/admin/diagnose" element={<Protected><DiagnosePage /></Protected>} />
          <Route path="/admin/integrations" element={<Protected adminOnly><IntegrationsPage /></Protected>} />
          <Route path="/admin/laaffic-parity" element={<Protected adminOnly><LaafficParityPage /></Protected>} />
          <Route path="/admin/laaffic-dispositions" element={<Protected adminOnly><LaafficDispositionsPage /></Protected>} />
          <Route path="/admin/laaffic-backfill" element={<Protected adminOnly><LaafficBackfillPage /></Protected>} />
          <Route path="/admin/laaffic" element={<Protected adminOnly><LaafficIntegrationPage /></Protected>} />
          <Route path="/admin/laaffic-unmapped" element={<Protected adminOnly><LaafficUnmappedPage /></Protected>} />
          <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
